import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Spinner, Badge } from 'react-bootstrap';
import { FaCheckCircle, FaClock, FaLock, FaChevronDown, FaChevronUp, FaArrowLeft } from 'react-icons/fa';
import axiosInstance from '../../utils/axios';
import FollowUpStage1 from '../../components/Followup/Followup1';
import Wrapper from '../../components/Wrapper';
import FollowUpStage2 from '../../components/Followup/Followup2';
import FollowUpStage3 from '../../components/Followup/Followup3';
import FollowUpStage4 from '../../components/Followup/Followup4';
import { getRole } from '../../utils/role';

const ConsolidatedFollowUpView = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();

  const [history, setHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const [openStage, setOpenStage] = useState(1);

  const fetchReportHistory = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/follow-up/report/${reportId}`);
      const completedResponses = response.data.data || [];

      const historyMap = {};
      completedResponses.forEach((item) => {
        historyMap[item.follow_up_stage] = item;
      });
      setHistory(historyMap);

      if (!historyMap[1]) {
        setOpenStage(1);
      } else if (!historyMap[2]) {
        setOpenStage(2);
      } else if (!historyMap[3]) {
        setOpenStage(3);
      } else if (!historyMap[4]) {
        setOpenStage(4);
      } else {
        setOpenStage(4);
      }
    } catch (error) {
      console.error("Failed to load follow-up history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (reportId) {
      fetchReportHistory();
    }
  }, [reportId]);

  const handleStageSuccess = (completedStage) => {
    fetchReportHistory();
    if (completedStage < 4) {
      setOpenStage(completedStage + 1);
    }
  };

  const stages = [
    { number: 1, title: 'Baseline Crop & Investment Profile', unlockAfter: 0 },
    { number: 2, title: 'Advisory Given & Farmer Response', unlockAfter: 1 },
    { number: 3, title: 'Advisory Progress, Disease/Pest & Investment', unlockAfter: 2 },
    { number: 4, title: 'Season Comparison', unlockAfter: 3 },
  ];

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Loading follow-up status...</p>
      </div>
    );
  }

  return (
    <Wrapper>
        <div className="container-fluid py-4">
        {/* Header Bar */}
        <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom">
            <div className="d-flex align-items-center gap-3">
            <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
                <FaArrowLeft className="me-1" /> Back to Reports
            </Button>
            <div>
                <h3 className="mb-0 font-weight-bold">Follow-Up Management</h3>
                <span className="text-muted small">Report ID: {reportId}</span>
            </div>
            </div>
        </div>

        {/* Accordion Sections for Stages 1, 2, and 3 */}
        <div className="space-y-4">
            {stages.map((stg) => {
            const isCompleted = !!history[stg.number];
            const isLocked = stg.unlockAfter > 0 && !history[stg.unlockAfter];
            const isOpen = openStage === stg.number;

            return (
                <div
                key={stg.number}
                className={`card border-0 shadow-sm mb-3 ${isLocked ? 'bg-light opacity-75' : ''}`}
                style={{ borderRadius: '8px', overflow: 'hidden', border: isOpen ? '2px solid #6f42c1' : '1px solid #e9ecef' }}
                >

                <div
                  className="card-header d-flex justify-content-between align-items-center p-3"
                  style={{
                    cursor: isLocked ? "not-allowed" : "pointer",
                    transition: "all 0.2s",
                    backgroundColor: isOpen ? "#6f42c1" : "#fff",
                    color: isOpen ? "#fff" : "#212529",
                  }}
                  onClick={() => !isLocked && setOpenStage(isOpen ? null : stg.number)}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span
                      className="badge px-2 py-1"
                      style={{
                        backgroundColor: isOpen ? "#fff" : "#6c757d",
                        color: isOpen ? "#6f42c1" : "#fff",
                      }}
                    >
                      Stage {stg.number}
                    </span>

                    <h6
                      className="mb-0 fw-bold"
                      style={{
                        color: isOpen ? "#fff" : "#212529",
                      }}
                    >
                      {stg.title}
                    </h6>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    {isCompleted && (
                      <Badge bg="success" className="d-flex align-items-center gap-1 px-2 py-1">
                        <FaCheckCircle className="me-1" />
                        Completed
                      </Badge>
                    )}

                    {!isCompleted && !isLocked && (
                      <Badge bg="warning" text="dark" className="d-flex align-items-center gap-1 px-2 py-1">
                        <FaClock className="me-1" />
                        Pending
                      </Badge>
                    )}

                    {isLocked && (
                      <Badge bg="secondary" className="d-flex align-items-center gap-1 px-2 py-1">
                        <FaLock className="me-1" />
                        Locked
                      </Badge>
                    )}

                    {!isLocked && (
                      <div style={{ color: isOpen ? "#fff" : "#6c757d" }}>
                        {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                      </div>
                    )}
                  </div>
                </div>

                {isOpen && !isLocked && (
                    <div className="card-body p-4 bg-white">
                        {isCompleted && (
                        <div className="alert alert-info py-2 px-3 mb-4 text-sm d-flex justify-content-between align-items-center">
                            <span>You have already submitted this stage. You can review or update your answers below.</span>
                            <small className="text-muted">
                            Submitted: {new Date(history[stg.number].created_at).toLocaleDateString()}
                            </small>

                            {getRole() === 'SUPPORT' && (
                              <small className="d-block mt-0.5 text-purple-700 font-semibold bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                                By: {history[stg.number].filled_by_role} ({history[stg.number].filled_by_name})
                              </small>
                            )}
                        </div>
                        )}
                        
                        {stg.number === 1 && (
                        <FollowUpStage1
                            reportId={reportId}
                            initialData={history[1]?.answers || null}
                            onSuccess={() => handleStageSuccess(1)}
                        />
                        )}
                        {stg.number === 2 && (
                        <FollowUpStage2
                            reportId={reportId}
                            initialData={history[2]?.answers || null}
                            onSuccess={() => handleStageSuccess(2)}
                        />
                        )}
                        {stg.number === 3 && (
                        <FollowUpStage3
                            reportId={reportId}
                            initialData={history[3]?.answers || null}
                            onSuccess={() => handleStageSuccess(3)}
                        />
                        )}
                        {stg.number === 4 && (
                        <FollowUpStage4
                            reportId={reportId}
                            initialData={history[4]?.answers || null}
                            onSuccess={() => handleStageSuccess(4)}
                        />
                        )}
                    </div>
                    )}
                </div>
            );
            })}
        </div>
        </div>
    </Wrapper>
  );
};

export default ConsolidatedFollowUpView;